import { InboxOutlined } from '@ant-design/icons';
import { Alert, Typography, Upload, message } from 'antd';
import type { UploadProps } from 'antd';

import { useUploadCertFilesMutation } from '../../redux/api/material-certs';

const { Dragger } = Upload;
const { Text } = Typography;

type Props = {
	onUploaded?: () => void;
};

/**
 * Drop packing-slip or mill-cert PDFs (or saved .eml files) into the queue.
 *
 * Same ingest as the mailbox, without needing Gmail configured. Nothing
 * attaches in ERP until someone matches.
 */
export default function UploadPanel({ onUploaded }: Props) {
	const [uploadFiles, { isLoading }] = useUploadCertFilesMutation();

	const customRequest: UploadProps['customRequest'] = async (options) => {
		const { file, onSuccess, onError } = options;
		const form = new FormData();
		form.append('files', file as File);
		try {
			const result = await uploadFiles(form).unwrap();
			const firstError = result.errors?.[0];
			if (firstError && !result.created) {
				onError?.(new Error(firstError.detail));
				message.error(
					`${firstError.filename}: ${firstError.detail}`
				);
				return;
			}
			if (result.created) {
				message.success(
					result.created === 1
						? 'File added to the review queue.'
						: `${result.created} files added to the review queue.`
				);
			} else if (result.skipped) {
				message.info('Already ingested; nothing new.');
			}
			if (firstError) {
				message.warning(`${firstError.filename}: ${firstError.detail}`);
			}
			onSuccess?.(result);
			onUploaded?.();
		} catch (error) {
			const data = (
				error as {
					data?: {
						detail?: string;
						errors?: { filename: string; detail: string }[];
					};
				}
			)?.data;
			const first = data?.errors?.[0];
			const detail = first
				? `${first.filename}: ${first.detail}`
				: data?.detail || 'Could not upload that file.';
			onError?.(error as Error);
			message.error(detail);
		}
	};

	return (
		<>
			<Alert
				type="info"
				showIcon
				style={{ marginBottom: 16 }}
				message="Reading only"
				description="Dropped files land in the review queue. Nothing attaches itself in ERP."
			/>
			<Dragger
				multiple
				accept=".pdf,.eml,application/pdf,message/rfc822"
				customRequest={customRequest}
				disabled={isLoading}
				showUploadList={{ showRemoveIcon: false }}
			>
				<p className="ant-upload-drag-icon">
					<InboxOutlined />
				</p>
				<p className="ant-upload-text">Drop .eml or PDF files here</p>
				<p className="ant-upload-hint">
					Packing slips, packing lists and mill certs, or a saved Alro or McMaster
					email. One or many.
				</p>
			</Dragger>
			<Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
				Use this when the mailbox is not configured, or to catch a file that
				never made it into production@.
			</Text>
		</>
	);
}
